'use client';
import { PresetWords } from '../../types/TrustScoreType';
import { MetricsType } from '../../types/CompanyType';
import { Button, Card, CardBody, CardFooter, CardHeader, CircularProgress, Divider, Link, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from "@heroui/react";
import { RefreshCcw, Lock } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { GaugeCircle } from '../ui/gauge-circle';
import { Fragment } from 'react';
import { getColor } from '@/lib/utils/ColorHelper';

interface CompanyReviewProps {
    company: any;
    trustScore: number;
    metricTypes: MetricsType[];
}

// Preset words for each variable
const presetWords: PresetWords = {
    businessRegistration: ['Business Address', 'Years in Business'],
    onlinePresence: ['Contact Number', 'Google Business Profile', 'Contact Email'],
    websiteScore: ['Company Website'],
    socialMediaPresence: ['Facebook'],
    businessOthers: ['CaseTrust'],
    employeeSatisfaction: ['Registration On LinkedIn'],
};


export default function CompleteReviewCard({
    isSignedIn,
    hasTrustScore = true,
    company,
    metricTypes,
    writeups,
    updatedAt,
}: {
    isSignedIn: boolean;
    hasTrustScore?: boolean;
    company: any;
    metricTypes: MetricsType[] 
    writeups: any;
    updatedAt: string;
}) {
    // Generate write-ups for each category based on the metrics
    // console.log('// metrics');
    // console.log(company.entity_name);
    // console.log(metricTypes);
    // const writeups = generateWriteups(metricTypes);
    // console.log('// writeups');
    // console.log(writeups);
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const router = useRouter();
    const excludedMetrics = [''];
    
    const getReviewContent = (score: number) => {
        if (score >= 90) {
            return (
                <div className="rounded-lg bg-green-100 p-4">
                    <h3 className="text-lg font-semibold text-green-800">Evaluation: High Trust</h3>
                    <p className="mt-2 text-green-700">
                        {company.entity_name} exhibits exceptional reliability and credibility. Their business registration with ACRA ensures that they are a legitimate
                        entity. They have extensive and reputable partnerships and collaborations, and hold multiple industry-recognized certifications and
                        accreditations, demonstrating their commitment to maintaining high standards.
                    </p>
                </div>
            );
        } else if (score >= 71) {
            return (
                <div className="rounded-lg bg-green-300 p-4">
                    <h3 className="text-lg font-semibold text-green-800">Evaluation: Low Risk</h3>
                    <p className="mt-2 text-green-700">
                        {company.entity_name} is generally considered reliable. Their business registration with ACRA ensures that they are a legitimate entity. They
                        have several reputable partnerships and some industry-recognized certifications, reflecting their adherence to standards.
                    </p>
                </div>
            );
        } else if (score === 70) {
            return (
                <div className="rounded-lg bg-default-300 p-4">
                    <h3 className="text-gray-800 text-lg font-semibold">Evaluation:Neutral</h3>
                    <p className="text-gray-700 mt-2">
                        {company.entity_name} has a neutral trust score, indicating that further data collection and analysis are necessary to assess their reliability
                        accurately. Their business registration with ACRA ensures that they are a legitimate entity.
                    </p>
                    <p className="text-gray-700 mt-2"></p>
                </div>
            );
        } else if (score >= 50) {
            return (
                <div className="rounded-lg bg-yellow-100 p-4">
                    <h3 className="text-orange-800 text-lg font-semibold">Evaluation: Risk</h3>
                    <p className="text-orange-700 mt-2">
                        {company.entity_name} may have certain aspects that warrant closer inspection. Their business registration with ACRA ensures that they are a
                        legitimate entity, but their partnerships and collaborations are limited, and they hold few or no industry-recognized certifications.
                    </p>
                    <p className="text-orange-700 mt-2">Verify all information carefully before proceeding with transactions with this company.</p>
                </div>
            );
        } else {
            return (
                <div className="rounded-lg bg-red-100 p-4">
                    <h3 className="text-lg font-semibold text-red-800">Evaluation: High Risk</h3>
                    <p className="mt-2 text-red-700">
                        {company.entity_name} may exhibit characteristics that suggest caution. Their business registration with ACRA ensures that they are a legitimate
                        entity. But exercise thorough due diligence before engaging in any transactions with this company.
                    </p>
                </div>
            );
        }
    };

    const BlurredContent = ({ children }: { children: React.ReactNode }) => {
        if (isSignedIn) {
            return <>{children}</>;
        }
        
        return (
            <div className="relative">
                <div className="blur-sm pointer-events-none">
                    {children}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/5 rounded-md">
                    <div className="bg-white/90 p-6 rounded-lg shadow-lg text-center max-w-md">
                        <Lock className="mx-auto mb-2 text-primary" />
                        <h3 className="text-lg font-semibold mb-2">Content Locked</h3>
                        <p className="mb-4 text-sm">Sign in to view the analysis and detailed metrics for {company.entity_name}</p>
                        <Button 
                            color="primary" 
                            size="sm"
                            as="a"
                            href={`/login?redirectUrl=/companies/${company.uen}/${company.slug}`}
                            rel="nofollow"
                            // onPress={() => router.push(`/login?redirectUrl=/companies/${company.uen}/${company.slug}`)}
                        >
                            Sign In to Continue
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (

        hasTrustScore && (
        <Card id="complete-company-report" className="rounded-lg p-4 shadow-lg mb-4">
            <CardHeader className="flex flex-col items-start gap-2">
            <h2 className="font-bold">Public Preview of {company.entity_name}</h2>
            <p className="mt-2 text-sm">
                This is only a preview of the TrustScore results for {company.entity_name}, showcasing a few facets of its business that we have analysed.
            </p>
            </CardHeader>
            <Divider />
            {/* Display Progress Bar for each metric */}
            <CardBody className="space-y-2">
                <BlurredContent>
                    {metricTypes
                        // .filter((a) => !excludedMetrics.includes(a.scoring_metric_type_name))
                        .map((metricType, index) => (
                                <Fragment key={index}>
                                    <div className="mx-auto items-center flex md:w-full flex-col md:flex-row text-center md:text-left">
                                        <GaugeCircle
                                            max={100}
                                            min={0}                                        
                                            value={metricType.score}
                                            gaugePrimaryColor={getColor(metricType.score, 'hex')}
                                            gaugeSecondaryColor="rgba(0, 0, 0, 0.1)"
                                        />
                                        <div className="p-4 w-full">
                                            <label className="text-gray-900 font-semibold">{metricType.scoring_metric_type_name}</label>
                                            {writeups && writeups.combinedWriteups
                                                .filter((writeup: any) => writeup.name === metricType.scoring_metric_type_name)
                                                .map((writeup: any, index: number) => (
                                                    <div key={index} className="mt-1 text-left">
                                                        <p className="text-gray-700 text-balance inline text-sm">{writeup.writeup}</p>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>                               
                                <Divider />
                            </Fragment>
                        ))}
                </BlurredContent>
            </CardBody>
            {/* updated at for trust score */}
            <CardFooter className="flex flex-col items-start gap-2">
   

                <div className="flex items-center gap-2 text-sm text-gray-700">
                    <p>TrustScore Last Scanned:</p>
                    <span>{updatedAt}</span>
                    { isSignedIn ? (                    
                            <Button size="sm" color="secondary" className='text-black' onPress={() => router.push(`/login?redirectUrl=/companies/${company.uen}/${company.slug}&message=Please log in to request a TrustScore update`)}>
                                <RefreshCcw size={20} />  Request for TrustScore Update
                            </Button>
                    ) : (
                        <>
                        <Button size="sm" color="secondary" className='text-black' onPress={onOpen}>
                            <RefreshCcw size={20} /> Request for TrustScore Update
                        </Button>
                        <Modal title="Refresh TrustScore" size="sm">
                            <div className="flex flex-col items-center space-y-4">
                                <p className="text-gray-700">TrustScore refresh has been initiated. Please check again later.</p>
                                <CircularProgress size="lg" value={0} maxValue={100} color="primary" />
                            </div>
                        </Modal>
                        <Modal
                            backdrop="opaque"
                            isOpen={isOpen}
                            isDismissable
                            onOpenChange={onOpenChange}
                        >
                            <ModalContent>
                                {(onClose: any) => (
                                    <>
                                        <ModalHeader className="flex flex-col gap-1 text-xl font-bold text-green-700 text-center">TrustScore update request sent</ModalHeader>
                                        <ModalBody>
                                            <p>TrustScore update has been initiated for {company.entity_name}</p>
                                            <p> Please check back later for the updated TrustScore</p>
                                        </ModalBody>
                                        <ModalFooter>
                                            <Button color="success" variant="solid" onPress={onClose}>
                                                ok
                                            </Button>
                                        </ModalFooter>
                                    </>
                                )}
                            </ModalContent>
                        </Modal>
                        </>
                    )}
                </div>
                {!company.verified && (
                        <Link href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/sign-up`}>
                            <Button size="sm" color="primary">
                                Protect Your Business Now
                            </Button>
                        </Link>
                    )}
            </CardFooter>
        </Card>
    ) || (
        <Card id="complete-company-report" className="rounded-lg p-4 shadow-lg mb-4">
            <CardHeader className="flex flex-col items-start gap-2">
            <h2 className="font-bold">Public Preview of {company.entity_name}</h2>
            <p className="mt-2 text-sm">
            TrustScore analysis is not available for {company.entity_name}.
            </p>
            </CardHeader>
        </Card>
    )
    );
}